import React, { useState, useCallback, useRef, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Loader2, Upload, Sparkles, Check, X, AlertCircle, FileText, Trash2, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIA_LABEL = {
  nomina: "Nómina",
  contrato: "Contrato",
  finiquito: "Finiquito",
  irpf: "IRPF",
  alta_ss: "Alta en Seguridad Social",
  baja_ss: "Baja en Seguridad Social",
  certificado: "Certificado",
  otros: "Otros",
};

const CATEGORIA_COLOR = {
  nomina: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  contrato: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  finiquito: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  irpf: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  alta_ss: "bg-teal-500/15 text-teal-300 border-teal-500/30",
  baja_ss: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  certificado: "bg-zinc-700/40 text-zinc-300 border-zinc-600",
  otros: "bg-zinc-700/40 text-zinc-400 border-zinc-700",
};

const ALLOWED_EXT = [".pdf", ".jpg", ".jpeg", ".png"];

/** Tab "Subir documentos con IA" — drag&drop multi-archivo con clasificación automática.
 *  - Acepta PDF/JPG/PNG arrastrados o seleccionados.
 *  - Cada archivo: analizando → analizado (editable) → confirmable.
 *  - Confirmar todo: bulk a /rrhh/documentos/confirmar-batch.
 */
export default function DragDropDocumentos({ onSavedReload }) {
  const [items, setItems] = useState([]);  // [{id, file, status, result, user_id, categoria, titulo, year, month, error}]
  const [users, setUsers] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    // Cargar TODOS los usuarios (incluir inactivos = colaboradores externos autocreados)
    api.get("/users").then(({ data }) => {
      const all = data || [];
      // Ordenar: activos primero, luego inactivos (externos) al final
      all.sort((a, b) => {
        if ((a.active !== false) !== (b.active !== false)) {
          return a.active !== false ? -1 : 1;
        }
        return (a.name || "").localeCompare(b.name || "");
      });
      setUsers(all);
    }).catch(() => toast.error("Error cargando usuarios"));
  }, []);

  const addFiles = useCallback(async (fileList) => {
    const accepted = Array.from(fileList).filter((f) => {
      const lower = f.name.toLowerCase();
      return ALLOWED_EXT.some((ext) => lower.endsWith(ext));
    });
    if (accepted.length === 0) {
      toast.error("Sólo PDF, JPG o PNG");
      return;
    }
    const newItems = accepted.map((f) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      file: f,
      filename: f.name,
      size: f.size,
      status: "queued",  // queued | analyzing | ready | error | saving | done | splitting
      result: null,
      user_id: null,
      categoria: "otros",
      titulo: f.name.replace(/\.[^.]+$/, ""),
      year: null,
      month: null,
      total_pages: 1,
      error: null,
      // Para nóminas mensuales combinadas (split flow)
      split_job_id: null,
      split_progress: null,
      split_result: null,
    }));
    setItems((prev) => [...prev, ...newItems]);
    // Procesar uno a uno (la IA no es paralelizable en free tier sin riesgo de rate-limit)
    for (const it of newItems) {
      await classifyOne(it.id, it.file);
    }
  }, []);

  const classifyOne = async (itemId, file) => {
    setItems((prev) => prev.map((p) => p.id === itemId ? { ...p, status: "analyzing" } : p));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/rrhh/documentos/clasificar", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      if (data.error) {
        setItems((prev) => prev.map((p) => p.id === itemId
          ? { ...p, status: "error", error: data.error } : p));
        return;
      }
      setItems((prev) => prev.map((p) => p.id === itemId ? {
        ...p,
        status: "ready",
        result: data,
        user_id: data.user_id_sugerido || null,
        categoria: data.tipo || "otros",
        titulo: data.titulo_sugerido || p.titulo,
        year: data.year || null,
        month: data.mes || null,
        total_pages: data.total_pages || 1,
      } : p));
    } catch (err) {
      setItems((prev) => prev.map((p) => p.id === itemId ? {
        ...p, status: "error", error: err?.response?.data?.detail || "Error IA",
      } : p));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length > 0) addFiles(e.dataTransfer.files);
  };

  const updateItem = (id, patch) => {
    setItems((prev) => prev.map((p) => p.id === id ? { ...p, ...patch } : p));
  };
  const removeItem = (id) => setItems((prev) => prev.filter((p) => p.id !== id));
  const retryItem = (it) => classifyOne(it.id, it.file);
  const clearDone = () => setItems((prev) => prev.filter((p) => p.status !== "done"));

  /** Divide un PDF de nómina mensual combinado en N nóminas individuales usando
   * el endpoint async (/rrhh/nominas/parse-pdf-async) + polling.
   */
  const splitNominaMensual = async (itemId) => {
    const it = items.find((p) => p.id === itemId);
    if (!it) return;
    if (!it.year || !it.month) {
      toast.error("Año/Mes son obligatorios para dividir");
      return;
    }
    updateItem(itemId, { status: "splitting", split_progress: { done: 0, total: it.total_pages || 0 } });
    try {
      const fd = new FormData();
      fd.append("file", it.file);
      fd.append("year", String(it.year));
      fd.append("month", String(it.month));
      const { data: kickoff } = await api.post("/rrhh/nominas/parse-pdf-async", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 60000,
      });
      const jobId = kickoff.job_id;
      updateItem(itemId, { split_job_id: jobId });
      // Polling cada 3s
      let attempts = 0;
      while (attempts < 200) {  // máx 10 min
        await new Promise((r) => setTimeout(r, 3000));
        attempts++;
        const { data: st } = await api.get(`/rrhh/nominas/parse-job/${jobId}`);
        updateItem(itemId, {
          split_progress: { done: st.progress_done || 0, total: st.progress_total || 0 },
        });
        if (st.status === "done") {
          updateItem(itemId, {
            status: "done",
            split_result: st.result,
          });
          const r = st.result || {};
          toast.success(`✅ ${r.creadas || 0} nóminas creadas${r.no_asociadas ? ` · ${r.no_asociadas} sin asociar` : ""}${r.autocreados_externos ? ` · ${r.autocreados_externos} externos autocreados` : ""}`);
          // NOTA: NO llamamos onSavedReload aquí porque el padre pone loading=true
          // y desmontaría este componente, perdiendo el split_result que queremos mostrar.
          // Las nóminas se ven en la pestaña "Nóminas IA" cuando el usuario navegue allí.
          return;
        }
        if (st.status === "error") {
          updateItem(itemId, { status: "error", error: st.error || "Error en parseo" });
          toast.error(`Error: ${st.error || "parseo fallido"}`);
          return;
        }
      }
      updateItem(itemId, { status: "error", error: "Timeout esperando parseo (>10min)" });
    } catch (err) {
      updateItem(itemId, { status: "error", error: err?.response?.data?.detail || err.message || "Error al dividir" });
      toast.error("Error al dividir el PDF");
    }
  };

  const guardarTodos = async () => {
    const validos = items.filter((p) => p.status === "ready" && p.user_id);
    if (validos.length === 0) {
      toast.error("No hay documentos listos con empleado asignado");
      return;
    }
    setSaving(true);
    try {
      // 1) Subir cada archivo a Cloudinary
      const { uploadToCloudinary } = await import("@/lib/cloudinary");
      const batch = [];
      for (const it of validos) {
        updateItem(it.id, { status: "saving" });
        try {
          const up = await uploadToCloudinary(it.file, "dnf/rrhh-documentos", "raw");
          const item = {
            user_id: it.user_id,
            categoria: it.categoria,
            titulo: it.titulo,
            url: up.secure_url,
            public_id: up.public_id,
            nombre_archivo: it.filename,
            tamano_bytes: it.size,
            year: it.year,
            month: it.month,
            fecha_documento: it.year && it.month
              ? `${it.year}-${String(it.month).padStart(2, "0")}-01`
              : (it.result?.fecha_documento || null),
            nomina_data: it.categoria === "nomina" ? it.result : null,
          };
          batch.push({ id: it.id, item });
        } catch (e) {
          const msg = e?.message || "Cloudinary error";
          updateItem(it.id, { status: "error", error: msg.slice(0, 220) });
        }
      }
      if (batch.length === 0) {
        toast.error("No se pudo subir ningún archivo");
        return;
      }
      // 2) Confirmar bulk en el backend
      const { data } = await api.post("/rrhh/documentos/confirmar-batch", {
        items: batch.map((b) => b.item),
      });
      toast.success(`✅ ${data.creados_documentos} documento(s) archivados${
        data.creadas_nominas > 0 ? ` · ${data.creadas_nominas} nómina(s) extraída(s)` : ""
      }${data.saltados > 0 ? ` · ${data.saltados} saltado(s)` : ""}`);
      // Marcar como done
      for (const b of batch) updateItem(b.id, { status: "done" });
      if (data.saltados > 0 && data.detalle_saltados?.length) {
        // muestra primer motivo de salto para depurar
        toast.warning(`Saltado: ${data.detalle_saltados[0].motivo}`);
      }
      onSavedReload?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error guardando batch");
      for (const it of validos) updateItem(it.id, { status: "ready" });
    } finally { setSaving(false); }
  };

  const listos = items.filter((p) => p.status === "ready" && p.user_id).length;
  const sinEmpleado = items.filter((p) => p.status === "ready" && !p.user_id).length;
  const procesando = items.filter((p) => p.status === "analyzing" || p.status === "saving").length;
  const dividiendo = items.filter((p) => p.status === "splitting").length;
  const errores = items.filter((p) => p.status === "error").length;
  const done = items.filter((p) => p.status === "done").length;

  return (
    <div className="space-y-4" data-testid="drag-drop-ia-panel">
      {/* Drop zone */}
      <div
        data-testid="drop-zone"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        className={`relative rounded-xl border-2 border-dashed transition-all cursor-pointer p-8 text-center ${
          dragging
            ? "border-violet-400 bg-violet-500/10"
            : "border-zinc-700 bg-zinc-900/30 hover:border-violet-500/50 hover:bg-zinc-900/50"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          multiple
          className="hidden"
          onChange={(e) => e.target.files && addFiles(e.target.files)}
          data-testid="file-input"
        />
        <div className="flex flex-col items-center gap-2">
          <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-violet-300" />
          </div>
          <div className="text-sm font-semibold text-zinc-100">
            Arrastra aquí PDFs / JPG / PNG · o haz click para seleccionar
          </div>
          <div className="text-xs text-zinc-500 max-w-md">
            La IA clasifica automáticamente el tipo (nómina / contrato / finiquito / IRPF / certificado), detecta el DNI del empleado y enruta a su cajón.
          </div>
        </div>
      </div>

      {/* Resumen */}
      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-400 bg-zinc-900/30 border border-zinc-800/60 rounded-lg p-3">
          <span className="font-mono">Total: <b className="text-zinc-100">{items.length}</b></span>
          {procesando > 0 && <span className="text-amber-300">⟳ Analizando: {procesando}</span>}
          {dividiendo > 0 && <span className="text-violet-300">⚡ Dividiendo: {dividiendo}</span>}
          <span className="text-emerald-300">✓ Listos: {listos}</span>
          {sinEmpleado > 0 && <span className="text-red-300">✗ Sin empleado: {sinEmpleado}</span>}
          {errores > 0 && <span className="text-red-300">✗ Errores: {errores}</span>}
          {done > 0 && <span className="text-zinc-500">✓ Guardados: {done}</span>}
          <div className="flex-1" />
          {done > 0 && (
            <Button size="sm" variant="ghost" onClick={clearDone}
              className="h-7 text-xs text-zinc-400 hover:text-zinc-100" data-testid="btn-clear-done">
              Limpiar guardados
            </Button>
          )}
          <Button size="sm" onClick={guardarTodos}
            disabled={saving || listos === 0}
            className="bg-emerald-500 text-zinc-950 hover:bg-emerald-400 disabled:opacity-40"
            data-testid="btn-guardar-todos">
            {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Guardando…</> :
              <><Check className="w-3.5 h-3.5 mr-1.5" /> Guardar {listos} listo(s)</>}
          </Button>
        </div>
      )}

      {/* Lista de items */}
      {items.length > 0 && (
        <div className="space-y-2" data-testid="items-list">
          {items.map((it) => (
            <ItemRow key={it.id} it={it} users={users}
              onUpdate={(patch) => updateItem(it.id, patch)}
              onRemove={() => removeItem(it.id)}
              onRetry={() => retryItem(it)}
              onSplit={() => splitNominaMensual(it.id)} />
          ))}
        </div>
      )}
    </div>
  );
}


function ItemRow({ it, users, onUpdate, onRemove, onRetry, onSplit }) {
  const isError = it.status === "error";
  const isReady = it.status === "ready";
  const isDone = it.status === "done";
  const isSplitting = it.status === "splitting";
  const noEmployee = isReady && !it.user_id;
  const isMultiPageNomina = isReady && it.categoria === "nomina" && (it.total_pages || 1) > 1;
  const borderCls = isDone ? "border-emerald-500/30 bg-emerald-500/5"
    : isError ? "border-red-500/40 bg-red-500/5"
    : isSplitting ? "border-violet-500/40 bg-violet-500/5"
    : isMultiPageNomina ? "border-violet-500/40 bg-violet-500/5"
    : noEmployee ? "border-red-500/40 bg-red-500/5"
    : isReady ? "border-zinc-800 bg-zinc-900/30"
    : "border-zinc-800 bg-zinc-900/30 opacity-80";

  return (
    <div className={`border rounded-lg p-3 transition-colors ${borderCls}`}
      data-testid={`item-row-${it.id}`}>
      <div className="flex items-start gap-3">
        {/* Icono estado */}
        <div className="shrink-0 mt-1">
          {it.status === "analyzing" ? <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
            : it.status === "saving" ? <Loader2 className="w-4 h-4 text-emerald-400 animate-spin" />
            : isSplitting ? <Loader2 className="w-4 h-4 text-violet-400 animate-spin" />
            : it.status === "done" ? <Check className="w-4 h-4 text-emerald-400" />
            : isError ? <AlertCircle className="w-4 h-4 text-red-400" />
            : isMultiPageNomina ? <Sparkles className="w-4 h-4 text-violet-400" />
            : noEmployee ? <AlertCircle className="w-4 h-4 text-red-400" />
            : <FileText className="w-4 h-4 text-zinc-400" />}
        </div>

        {/* Datos */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <div className="text-sm font-medium text-zinc-100 truncate" title={it.filename}>
              {it.filename}
            </div>
            <div className="text-[10px] text-zinc-500 font-mono shrink-0">
              {(it.size / 1024).toFixed(0)} KB
            </div>
          </div>

          {it.status === "analyzing" && (
            <div className="text-xs text-amber-300">Analizando con IA…</div>
          )}

          {isSplitting && (
            <div className="space-y-1.5" data-testid="splitting-progress">
              <div className="text-xs text-violet-300 flex items-center gap-2">
                <Sparkles className="w-3 h-3" />
                Dividiendo PDF en nóminas individuales…
                {it.split_progress?.total > 0 && (
                  <span className="font-mono text-violet-200">
                    {it.split_progress.done}/{it.split_progress.total} páginas
                  </span>
                )}
              </div>
              {it.split_progress?.total > 0 && (
                <div className="h-1.5 w-full bg-zinc-800 rounded overflow-hidden">
                  <div
                    className="h-full bg-violet-500 transition-all duration-300"
                    style={{ width: `${Math.round((it.split_progress.done / it.split_progress.total) * 100)}%` }}
                  />
                </div>
              )}
            </div>
          )}

          {isError && (
            <div className="flex items-center gap-2">
              <div className="text-xs text-red-300 flex-1">{it.error || "Error desconocido"}</div>
              <Button size="sm" variant="ghost" onClick={onRetry}
                className="h-6 text-xs text-amber-300 hover:bg-amber-500/10" data-testid="btn-retry">
                <RefreshCw className="w-3 h-3 mr-1" /> Reintentar
              </Button>
            </div>
          )}

          {(isReady || isDone) && (
            <div className="space-y-2">
              {/* Banner especial para PDF combinado de nóminas mensuales */}
              {isMultiPageNomina && (
                <div className="rounded-md border border-violet-500/40 bg-violet-500/10 p-2.5" data-testid="banner-multipage-nomina">
                  <div className="flex items-start gap-2">
                    <Sparkles className="w-4 h-4 text-violet-300 mt-0.5 shrink-0" />
                    <div className="flex-1 text-xs space-y-1.5">
                      <div className="text-violet-100">
                        <b>PDF con {it.total_pages} páginas detectado.</b> Parece la <b>nómina mensual combinada</b> de la gestoría.
                        Si lo divides, la IA leerá página a página y creará <b>{it.total_pages} nóminas individuales</b> asociadas a cada empleado por su DNI.
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button size="sm" onClick={onSplit}
                          disabled={!it.year || !it.month}
                          className="h-7 text-xs bg-violet-500 text-white hover:bg-violet-400 disabled:opacity-50"
                          data-testid="btn-split-nomina-mensual">
                          <Sparkles className="w-3 h-3 mr-1" /> Dividir en {it.total_pages} nóminas
                        </Button>
                        {(!it.year || !it.month) && (
                          <span className="text-[10px] text-amber-300">↓ Indica Mes y Año primero</span>
                        )}
                        <span className="text-[10px] text-zinc-500">
                          (alternativa: <b>"Guardar"</b> como documento único para Fernando)
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Datos detectados */}
              <div className="flex flex-wrap items-center gap-2 text-[10px]">
                <span className={`px-2 py-0.5 rounded border ${CATEGORIA_COLOR[it.categoria]}`}>
                  {CATEGORIA_LABEL[it.categoria]}
                </span>
                {it.result?.confianza !== undefined && (
                  <span className="text-zinc-500 font-mono">
                    Confianza IA: {(it.result.confianza * 100).toFixed(0)}%
                  </span>
                )}
                {it.result?.dni_nie && (
                  <span className="text-zinc-400 font-mono">DNI: {it.result.dni_nie}</span>
                )}
                {it.result?.nombre_completo && (
                  <span className="text-zinc-300">{it.result.nombre_completo}</span>
                )}
              </div>

              {/* Campos editables (sólo si no done) */}
              {!isDone && (
                <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                  <div className="md:col-span-4">
                    <Label className="text-[10px] text-zinc-500">Empleado</Label>
                    <Select value={it.user_id || "__NONE__"}
                      onValueChange={(v) => onUpdate({ user_id: v === "__NONE__" ? null : v })}>
                      <SelectTrigger
                        className={`h-8 text-xs ${noEmployee
                          ? "bg-red-500/10 border-red-500/50 text-red-200"
                          : "bg-zinc-950 border-zinc-800"}`}
                        data-testid={`select-empleado-${it.id}`}>
                        <SelectValue placeholder="⚠ Sin asignar" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-64">
                        <SelectItem value="__NONE__">— Sin asignar —</SelectItem>
                        {/* Activos (equipo interno) */}
                        {users.filter((u) => u.active !== false).map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.name}{u.dni_nie ? ` · ${u.dni_nie}` : ""}
                          </SelectItem>
                        ))}
                        {/* Externos / autocreados (inactive) */}
                        {users.filter((u) => u.active === false).length > 0 && (
                          <div className="px-2 py-1 text-[10px] uppercase text-amber-400/70 border-t border-zinc-800 mt-1">
                            Colaboradores externos
                          </div>
                        )}
                        {users.filter((u) => u.active === false).map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.name}{u.dni_nie ? ` · ${u.dni_nie}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-3">
                    <Label className="text-[10px] text-zinc-500">Tipo</Label>
                    <Select value={it.categoria} onValueChange={(v) => onUpdate({ categoria: v })}>
                      <SelectTrigger className="bg-zinc-950 border-zinc-800 h-8 text-xs"
                        data-testid={`select-tipo-${it.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                        {Object.entries(CATEGORIA_LABEL).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-1">
                    <Label className="text-[10px] text-zinc-500">Mes</Label>
                    <Input type="number" min="1" max="12" value={it.month || ""}
                      onChange={(e) => onUpdate({ month: e.target.value ? parseInt(e.target.value, 10) : null })}
                      className="bg-zinc-950 border-zinc-800 h-8 text-xs" data-testid={`input-mes-${it.id}`} />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[10px] text-zinc-500">Año</Label>
                    <Input type="number" min="2020" max="2099" value={it.year || ""}
                      onChange={(e) => onUpdate({ year: e.target.value ? parseInt(e.target.value, 10) : null })}
                      className="bg-zinc-950 border-zinc-800 h-8 text-xs" data-testid={`input-year-${it.id}`} />
                  </div>
                  <div className="md:col-span-12">
                    <Label className="text-[10px] text-zinc-500">Título</Label>
                    <Input value={it.titulo} onChange={(e) => onUpdate({ titulo: e.target.value })}
                      className="bg-zinc-950 border-zinc-800 h-8 text-xs"
                      data-testid={`input-titulo-${it.id}`} />
                  </div>
                </div>
              )}

              {isDone && it.split_result && (
                <div className="text-xs text-emerald-300 space-y-0.5" data-testid="split-result-summary">
                  <div>✓ <b>{it.split_result.creadas || 0} nóminas creadas</b> · {it.split_result.total_paginas || 0} páginas procesadas</div>
                  {(it.split_result.no_asociadas || 0) > 0 && (
                    <div className="text-amber-300">⚠ {it.split_result.no_asociadas} página(s) sin empleado asociado</div>
                  )}
                  {(it.split_result.autocreados_externos || 0) > 0 && (
                    <div className="text-violet-300">+ {it.split_result.autocreados_externos} colaborador(es) externo(s) autocreado(s)</div>
                  )}
                </div>
              )}

              {isDone && !it.split_result && (
                <div className="text-xs text-emerald-400">
                  ✓ Archivado en el cajón de {users.find((u) => u.id === it.user_id)?.name || "—"}
                  {it.categoria === "nomina" ? " · Nómina registrada con importes" : ""}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Eliminar */}
        {!isDone && (
          <Button size="sm" variant="ghost" onClick={onRemove}
            className="h-7 w-7 p-0 text-zinc-500 hover:text-red-300 shrink-0"
            data-testid={`btn-remove-${it.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
